<template>

 <div>
     <el-button ref="pswButton" type="warning" v-if="(change || newPsw || isSuper() || form.login === profile().login)" :disabled="(!newPsw && !change) || disabled" @click="changePassword">{{title}} Password</el-button>

     <!-- XOL-392: "Reset Password" is hidden for now, on Carlos's instruction.
          It was never operable: the button was hard-disabled (`:disabled="!isSuper() || true"`)
          because LoginController::resetPassword() was an empty method body behind a live
          role:super route. This rework moves the credential surface onto the System Details
          tab, which would have put a permanently-inert danger button directly beside the live
          Change Password on every contact a super opens. The resetPassword()/resetPasswordSuccess()
          methods went with it (see the methods block).

          XOL-602/603/604: the reset action now exists, and it does NOT live here. It is
          "Send reset link" / "Send invitation" in PasswordLinkAction (System Details for a
          super, the Contact tab for a manager): it e-mails the contact a single-use link
          rather than setting a password, so it has no place in a dialog whose job is to type
          one. The empty API stub PUT /password/reset/{id} was removed.

          PasswordForm's own dead changePassword()/resetPassword() went with it. -->

   <!-- XOL-392 (2026-08-20): 440px gives a body content column of exactly 400px
        (Element's .el-dialog__body padding is 30px 20px), which is the password
        widget's own max-width — so the field fills the column edge to edge and
        the label sits flush at its left. A password dialog does not need half
        of a 1920px screen. -->
   <!-- XOL-392 (2026-08-20): destroy-on-close is REQUIRED, not tidiness.
        Reported by Carlos: "Change password modal does not reset when closed."
        clearCredential() nulls form.password/c_password on every dismissal
        path, but that only clears the MODEL. vue-password-strength-meter keeps
        its own internal `password` in data() and its `value` watcher is
        `strengthMeterOnly && emitValue('input', e)` — we do not set
        strengthMeterOnly, so the watcher is inert and the internal copy is only
        ever written by user input. The visible text and the strength meter
        therefore survived a close/reopen even though the bound value was null.
        The dialog's slot is never unmounted otherwise (no v-if on the dialog
        itself), so destroying the content on close is what actually resets the
        third-party widget. Element bumps an internal key on close to do it.
        Alternatives rejected: setting strengthMeterOnly changes what renders,
        and reaching into the child's data would be a private-API dependency. -->
   <!-- XOL-392 (2026-08-20): append-to-body is REQUIRED, not cosmetic.
        Reported by Carlos: the dialog looked wrong on a contact's System Details
        tab but right on /profile. On System Details the trigger button — and
        therefore this whole dialog — sits inside the left block's
        `<el-form :inline="true">`. Element's theme has
        `.el-form--inline .el-form-item { display: inline-block; margin-right: 10px }`,
        a DESCENDANT selector, so it reached through the dialog into
        PasswordForm's own `:inline="false"` form and collapsed both password
        fields to shrink-to-fit. On /profile the dialog hangs off the Contact
        tab with no inline-form ancestor, which is why only one of the two
        screens was affected. This is the XOL-451 leak again — third variant.
        Teleporting the dialog to <body> takes it out of that ancestor chain
        entirely, so it renders identically wherever the trigger happens to be
        mounted. Scoped styles are unaffected (the data attribute travels with
        the elements), as are destroy-on-close and the focus handlers, which
        resolve through $refs rather than position. -->
   <el-dialog ref="pswDialog" class="text-left" :title="title + ' Password'" :visible.sync="changePsw"
     :width="isMediumOrLess ? '92%' : '440px'"
     append-to-body
     destroy-on-close
     :close-on-click-modal="false" :before-close="beforeDialogClose"
     @opened="focusFirstField" @closed="focusButton">

     <template v-if="!loading">
       <password-form v-model="form" :change="change" :new-psw="newPsw" :inline="false" :toggle="toggle"
         @confirm-blur="confirmTouched = true" />

       <!-- XOL-392 (2026-08-20): reported by Carlos — "Change password modal
            starts with error". The alert used to be `v-if="!passwordsMatch"`,
            and passwordsMatch is false for an EMPTY form, so the error was the
            dialog's default state: it opened red, accusing the user before they
            had typed anything. It is now the mismatch case only, and only after
            the confirm field has been blurred — a confirm field mismatches by
            construction on every keystroke, so an ungated alert would go red at
            character 1 of a perfectly correct entry. Sticky once touched, so it
            then updates live and clears the moment the values agree.
            The length rule is no longer an alert at all; it is the permanent
            hint under the first field.

            XOL-602/603/604 (D8): the alert now reports the first problem of the one
            password rule (lib/passwordPolicy.js): over 72 characters, contains the
            Login ID, or mismatch. "Too short" still has no alert (the hint covers it). -->
       <el-alert v-if="showMismatch" type="error" show-icon :closable="false"
         id="password-rules" role="alert" style="margin-top: 4px;"
         :title="problemMessage" />
     </template>

     <div v-else><i class="el-icon-loading"></i>&nbsp;Loading&hellip;</div>

     <template #footer><span class="dialog-footer">
       <el-button @click="cancelPsw">Cancel</el-button>
       <el-button type="success" @click="savePsw" :disabled="!passwordsMatch">Save</el-button>
     </span></template>
   </el-dialog>
 </div>


 </div>

</template>

<script>

import PostMixins from '@/mixins/PostMixins'

import PasswordForm from '@/components/login/PasswordForm'
import * as authLib from '@/lib/auth'
import { passwordProblem, passwordProblemText, visiblePasswordProblem } from '@/lib/passwordPolicy'

export default {

  mixins: [PostMixins],

  components: {
    PasswordForm
  },

  props: {
    value: {
      required: true
    },

    toggle: {
      type: Boolean,
      default: true
    },

    change: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: true
    },

    newPsw: {
      type: Boolean,
      default: false
    },

  },

  watch: {

    value: function (d) {
      this.form = d
//      console.log('PasswordDialog', this.form, this.change)
    }

  },


  computed: {
    // XOL-392: threshold 7 -> 8, matching ChangePasswordRequest, RegisterRequest,
    // RegistrationRequest (all min:8) and Register.vue's own passwordsMatch.
    // PasswordDialog was the lone outlier at 7, and the new inline create path
    // (which enforces 8 too) sits on the same tab now — one credential surface
    // accepting 7 while another required 8 would be incoherent.
    //
    // XOL-602/603/604 (D8): now the one shared rule, lib/passwordPolicy.js: trimmed,
    // 8 to 72 characters, not containing the Login ID (case-insensitive), and equal to
    // its confirmation. The Login ID is the record's own (`form.login`).
    passwordsMatch: function () {
      return passwordProblem(this.form.password, this.form.c_password, this.form.login) === null
    },

    // XOL-392 (2026-08-20): the alert's condition. Deliberately NOT
    // `!passwordsMatch` — that is false for an empty form, which is what made
    // the dialog open red. Requires the confirm field to have been blurred at
    // least once, so a correct entry is never called wrong mid-keystroke.
    // `too_short` never alerts (the permanent hint covers it), and a mismatch
    // needs both fields to have content, so an empty confirm is not an accusation.
    // XOL-602/603/604: any other problem (too long, contains the Login ID) alerts
    // once touched. The gate itself is visiblePasswordProblem (lib/passwordPolicy.js),
    // shared with the set/reset and invite pages.
    showMismatch () {
      return visiblePasswordProblem(this.form.password, this.form.c_password, this.form.login, this.confirmTouched) !== null
    },

    // The alert's title: the first problem's text from lib/passwordPolicy.js.
    problemMessage () {
      return passwordProblemText(visiblePasswordProblem(this.form.password, this.form.c_password, this.form.login, this.confirmTouched))
    }
  },




  methods: {
    savePsw () {

      if (this.changePsw) {
         this.changePsw = false

         // XOL-392 (2026-08-20): trim BOTH, not just `password`. Previously
         // only the new password was trimmed, and after passwordsMatch had
         // already compared the untrimmed values — so "secret1 " typed
         // identically into both fields validated, then stored "secret1", and
         // the user could not sign in with what they had typed. Register.vue
         // trims both; match it.
         this.form.password = this.form.password.trim()
         if (this.form.c_password) {
           this.form.c_password = this.form.c_password.trim()
         }

         // XOL-391: never post without an id. PUT /password/change/{id} needs a
         // saved contact; with no id saveForm() would fall through to a POST at
         // /password/change, which matches no route. ContactDetail already hides
         // this control while adding (setting a credential at create time is
         // deferred to XOL-392), so this is a defensive stop for any other
         // caller rather than a reachable path today.
         if (!this.form.id) {
           this.$message({
             type: 'warning',
             message: 'Save the contact first, then set its password.'
           })
           return
         }

         let pass = {
           password: this.form.password,
           email: this.form.email
         }

         // XOL-371: allowViewOnly=true (last arg) — a view-only user may rotate
         // their OWN password; the button only shows for the own record and the
         // API keeps /password/change self-only + exempt from the write-block.
         //
         // XOL-392 (2026-08-20): the success-callback argument used to be
         // `this.changePasswordSuccess`, a method that does not exist anywhere
         // in src/. It was a silent no-op only because PostMixins.send() guards
         // the call; the generic success toast still fires.
         //
         // XOL-602/603/604 (D9): a password change signs the account out everywhere
         // else. When the caller changes their OWN password the API re-issues their
         // tokens in the 200 body so this device stays signed in; adoptReissuedTokens
         // stores them. A change for another contact returns none, and nothing is adopted.
         this.saveForm(this, '/password/change', this.form.id, pass,  'Change Password', this.adoptReissuedTokens, null, true, null, false, true)

         // XOL-392 (2026-08-20): savePsw() closes the dialog by setting
         // changePsw directly, which by design does NOT run before-close — so
         // clearCredential() never ran on the Save path and reopening the
         // dialog showed the password just set, prefilled, with Save enabled.
         // `pass` is already a copy, so the in-flight request is unaffected.
         // This also resets confirmTouched, which showMismatch depends on.
         this.clearCredential()
      }

    },

    // XOL-602/603/604 (D9): the 200 body of PUT /password/change/{id} carries a fresh
    // `access_token` + `refresh_token` ONLY when the caller changed their own password
    // (the API ended every other session, this one included, and re-issued this one).
    // Adopt them, or this device is signed out on its next request. setTokens decodes the
    // claims itself, as refresh() does. A response without both tokens is left alone, and so
    // is one for a contact other than the signed-in user: tokens are adopted only when the
    // record being changed IS the profile (GET /login's `user` carries its `id`).
    adoptReissuedTokens (data) {
      var self = this.form && this.profile() && Number(this.form.id) === Number(this.profile().id)

      if (self && data && data.access_token && data.refresh_token) {
        authLib.setTokens({ accessToken: data.access_token, refreshToken: data.refresh_token })
      }
    },

    changePassword () {
      this.changePsw = true
    },

    // XOL-391: `c_password` is never part of a contact record (it is not a DB
    // column), so on a fresh form OR a freshly fetched contact the key is
    // absent and `v-model="form.c_password"` would write a NON-reactive
    // property — passwordsMatch would never re-evaluate as the confirm field
    // is typed, and Save would stay permanently disabled. Seed both keys
    // reactively. Extracted so both created() AND formChange() (XOL-392,
    // FLAG 3) can call it — formChange() REPLACES this.form with the fetched
    // contact, which has no password/c_password keys of its own ($hidden).
    seedCredentialKeys (target) {
      if (target) {
        if (!Object.prototype.hasOwnProperty.call(target, 'password')) {
          this.$set(target, 'password', null)
        }
        if (!Object.prototype.hasOwnProperty.call(target, 'c_password')) {
          this.$set(target, 'c_password', null)
        }
      }
    },

    formChange (data) {
      this.loading = false
      this.form = data
      this.seedCredentialKeys(this.form)
    },

    // XOL-392 FLAG 4: fetchData's 4th arg. Without it a failed fetch (403,
    // 404, network) leaves `loading` true forever — v-if="!loading" never
    // renders the fields, and the dialog opens as a title, a Cancel and a
    // permanently disabled Save with no explanation. fetchData's own $notify
    // still fires and explains the failure; this just unsticks the dialog.
    fetchFailed () {
      this.loading = false
    },

    // XOL-392 (carrying D9 forward): clear the typed credential on every
    // dismissal path so it does not linger on whichever object we hold.
    //
    // Which object that IS depends on whether the fetch ran. Before it
    // resolves, `form` is ContactDetail's object by reference (v-model, no
    // $emit('input')) — on /profile that is the live Vuex profile(). After it
    // resolves, formChange() has replaced the reference with the fetched
    // contact, so this clears the dialog's own copy and NOT ContactDetail's.
    // That is why dropping `password` from saveSelfProfile()'s payload is the
    // real protection for the /profile leg, not a redundant belt-and-braces.
    clearCredential () {
      this.form.password = null
      this.form.c_password = null
      // XOL-392 (2026-08-20): reset the blur gate too, so a reopened dialog
      // starts clean rather than re-showing the mismatch alert from last time.
      this.confirmTouched = false
    },

    cancelPsw () {
      this.clearCredential()
      this.changePsw = false
    },

    // el-dialog runs before-close for the X icon, ESC and the mask click, and
    // NOT when `visible` is set programmatically — which is exactly right:
    // savePsw() sets changePsw = false itself and must not clear the value it
    // just submitted. Do NOT bind @close; that fires on the Save path too.
    beforeDialogClose (done) {
      this.clearCredential()
      done()
    },

    // XOL-392 a11y: 2.15.0's el-dialog focuses nothing on open. Focus the
    // first password field once the dialog has finished opening (fields are
    // gated by v-if="!loading", so the query can legitimately find nothing).
    // .Password__field is vue-password-strength-meter's defaultClass on the
    // real <input>.
    focusFirstField () {
      var el = this.$refs.pswDialog.$el.querySelector('.Password__field')
      if (el) {
        el.focus()
      }
    },

    // XOL-392 a11y: 2.15.0's el-dialog does not restore focus on close, for
    // any of the four exits (Save, Cancel, X, ESC) — @closed fires for all of
    // them. Return focus to the button that opened the dialog.
    focusButton () {
      if (this.$refs.pswButton) {
        this.$refs.pswButton.$el.focus()
      }
    },

    // XOL-392: resetPassword()/resetPasswordSuccess() removed with the button.
    // They targeted PUT /password/reset/{id}, whose handler
    // (LoginController::resetPassword) is an empty method body — keeping live
    // code wired to a stub endpoint is the same liability as the dead control
    // itself. Restoring the feature needs the markup back regardless, so the
    // methods saved nothing. See the comment above the button in <template>.

  },

  data () {
    return {
      changePsw: false,
      loading: true,
      // XOL-392 (2026-08-20): has the confirm field been blurred at least once
      // this open? Gates the mismatch alert — see showMismatch. Sticky until
      // clearCredential() resets it on close/cancel/save.
      confirmTouched: false,
      form: this.value,
      title: (this.newPsw ? 'New' : 'Change')
    }
  },

  created () {
    this.seedCredentialKeys(this.form)

    // XOL-391: only fetch when there is a real contact to fetch. ContactDetail
    // mounts this with a bare `change` attribute (always true), including in
    // add mode, so a new contact has no `form.id` — the URL became
    // /contacts/undefined and the API answered 422 ("Contact not found"),
    // surfacing an error toast before the user had typed anything. Guard here
    // rather than at the call site so every consumer is covered.
    //
    // `loading` gates <password-form v-if="!loading">, and it was only ever
    // cleared by formChange() — the SUCCESS callback of that fetch. With no
    // fetch to run, a new contact left loading true forever, so the dialog
    // opened completely empty and no password could be set at all. Clear it
    // explicitly when there is nothing to load.
    if (this.change && this.form && this.form.id) {
      // XOL-392 FLAG 4: errorCallback clears `loading` on a failed fetch too
      // (403/404/network), so the dialog never strands empty.
      this.fetchData('/contacts/' + this.form.id, this.formChange, 'Contact', this.fetchFailed)
    } else {
      this.loading = false
    }
  }
}

</script>
